import React, { useState, useEffect, useRef } from 'react';
import { useAppSelector } from '../../store/hooks';
import { CText, CLoader } from '../../components/common';
import { IoMenu, IoSparkles, IoAdd } from 'react-icons/io5';
import {
  queryStockResearch,
  getAllCompanyNames,
  ResearchQuery,
  ResearchResult,
} from '../../api/copilot';
import {
  getConversations,
  getConversation,
  createConversation,
  deleteConversation,
  addMessage,
  Conversation,
  Message,
  Source,
} from '../../api/chat';
import ChatSidebar from './components/ChatSidebar';
import ChatMessage from './components/ChatMessage';
import ChatInput from './components/ChatInput';

const Copilot: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const authToken = useAppSelector((state) => state.user.authToken);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);

  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(true);

  // Company selection state
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [allCompanies, setAllCompanies] = useState<string[]>([]);

  // Load companies on mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const companies = await getAllCompanyNames();
        setAllCompanies(companies);
      } catch (err) {
        console.error('Failed to load companies:', err);
      }
    };
    loadCompanies();
  }, []);

  // Load conversations on mount
  useEffect(() => {
    const loadConversations = async () => {
      if (!authToken) {
        setConversationsLoading(false);
        return;
      }
      try {
        const convs = await getConversations(authToken);
        setConversations(convs);
      } catch (err) {
        console.error('Failed to load conversations:', err);
      } finally {
        setConversationsLoading(false);
      }
    };
    loadConversations();
  }, [authToken]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Select a conversation and load its messages
  const handleSelectConversation = async (id: number) => {
    if (!authToken) return;
    try {
      setLoading(true);
      const conv = await getConversation(authToken, id);
      setCurrentConversationId(id);
      setMessages(conv.messages || []);
      setSidebarOpen(false);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    } finally {
      setLoading(false);
    }
  };

  // Start a new chat
  const handleNewChat = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setSelectedCompanies([]);
    setSidebarOpen(false);
  };

  // Delete a conversation
  const handleDeleteConversation = async (id: number) => {
    if (!authToken) return;
    try {
      await deleteConversation(authToken, id);
      setConversations(conversations.filter(c => c.id !== id));
      if (currentConversationId === id) {
        handleNewChat();
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  // Send a message
  const handleSend = async (text: string, options: { deepSearch: boolean; multiAgent: boolean; topK: number }) => {
    if (!text.trim()) return;

    // Add user message immediately
    const userMessage: Message = {
      role: 'user',
      text: text,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    try {
      // Create conversation if needed
      let conversationId = currentConversationId;
      if (!conversationId && authToken) {
        const title = text.length > 50 ? text.substring(0, 50) + '...' : text;
        const newConv = await createConversation(authToken, title);
        conversationId = newConv.id;
        setCurrentConversationId(conversationId);
        setConversations(prev => [{ ...newConv, title }, ...prev]);
      }

      // Save user message to backend
      if (conversationId && authToken) {
        await addMessage(authToken, conversationId, 'user', text);
      }

      // Query the research API
      const params: ResearchQuery = {
        query: text,
        top_k: options.topK,
        deep_search: options.deepSearch,
        return_pdf: false,
        enable_multi_agent: options.multiAgent,
      };

      if (selectedCompanies.length > 0) {
        params.company_names = selectedCompanies;
      }

      const result: ResearchResult = await queryStockResearch(params);

      // Create assistant message with sources
      const sources: Source[] = result.sources?.map(s => ({
        type: s.type,
        title: s.title || s.company || 'Source',
        company: s.company,
        url: s.url,
        similarity: s.similarity,
        report_type: s.report_type,
      })) || [];

      const assistantMessage: Message = {
        role: 'assistant',
        text: result.llm_response || 'Sorry, I could not generate a response.',
        created_at: new Date().toISOString(),
        sources: sources,
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Save assistant message to backend
      if (conversationId && authToken) {
        await addMessage(authToken, conversationId, 'assistant', assistantMessage.text);
      }
    } catch (err: any) {
      console.error('Failed to send message:', err);
      const errorMessage: Message = {
        role: 'assistant',
        text: `Error: ${err.message || 'Failed to get response'}`,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex h-screen"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      {/* Sidebar */}
      <ChatSidebar
        conversations={conversations}
        currentConversationId={currentConversationId}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSelectConversation={handleSelectConversation}
        onNewChat={handleNewChat}
        onDeleteConversation={handleDeleteConversation}
        colors={colors}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div
          className="flex items-center justify-between p-4"
          style={{
            backgroundColor: colors.inputBackground,
            borderBottom: `1px solid ${colors.borderColor}`,
          }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg lg:hidden"
              style={{ backgroundColor: colors.backgroundColor }}
            >
              {React.createElement(IoMenu as any, { size: 24, color: colors.textColor })}
            </button>
            <div className="flex items-center gap-2">
              {React.createElement(IoSparkles as any, { size: 24, color: colors.primary })}
              <CText type="B18" color={colors.textColor}>
                Research Copilot
              </CText>
            </div>
          </div>
          <button
            onClick={handleNewChat}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
            style={{ backgroundColor: colors.primary, color: colors.white }}
          >
            {React.createElement(IoAdd as any, { size: 18 })}
            <span className="hidden sm:inline text-sm font-medium">New Chat</span>
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4">
          {conversationsLoading ? (
            <div className="flex items-center justify-center h-full">
              <CLoader />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center max-w-md mx-auto">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: colors.primary + '20' }}
              >
                {React.createElement(IoSparkles as any, { size: 32, color: colors.primary })}
              </div>
              <CText type="B20" color={colors.textColor}>
                Welcome to Research Copilot
              </CText>
              <CText type="r14" color={colors.grayScale500} style={{ marginTop: 8 }}>
                Ask questions about stocks, companies, financials, and market trends.
                I'll search through annual reports, concalls, and the latest news.
              </CText>
              <div className="flex flex-wrap justify-center gap-2 mt-6">
                {['What are TCS earnings trends?', 'Compare HDFC and ICICI Bank', 'IT sector outlook'].map(
                  (suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => handleSend(suggestion, { deepSearch: false, multiAgent: true, topK: 5 })}
                      className="px-4 py-2 rounded-full text-sm transition-colors"
                      style={{
                        backgroundColor: colors.inputBackground,
                        border: `1px solid ${colors.borderColor}`,
                        color: colors.textColor,
                      }}
                    >
                      {suggestion}
                    </button>
                  )
                )}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto">
              {messages.map((message, idx) => (
                <ChatMessage key={idx} message={message} colors={colors} />
              ))}
              {loading && (
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: colors.grayScale400 }}
                  >
                    {React.createElement(IoSparkles as any, { size: 16, color: colors.white })}
                  </div>
                  <div
                    className="p-4 rounded-2xl rounded-tl-sm"
                    style={{
                      backgroundColor: colors.inputBackground,
                      border: `1px solid ${colors.borderColor}`,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <CLoader />
                      <CText type="r14" color={colors.grayScale500}>
                        Researching...
                      </CText>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <ChatInput
          onSend={handleSend}
          isLoading={loading}
          selectedCompanies={selectedCompanies}
          onCompanySelect={setSelectedCompanies}
          allCompanies={allCompanies}
          colors={colors}
        />
      </div>
    </div>
  );
};

export default Copilot;
